import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import type { SyncQueueItem, SyncResult } from '@/types';
import { syncManager } from '@/services/sync/syncManager';
import { useOffline } from './OfflineContext';
import { useAuth } from './AuthContext';
import { SYNC_CONFIG } from '@/constants';

interface SyncContextType {
  pendingCount: number;
  isSyncing: boolean;
  lastSyncTime: Date | null;
  syncError: string | null;
  syncNow: () => Promise<SyncResult>;
  addToQueue: (item: Omit<SyncQueueItem, 'id' | 'timestamp' | 'status' | 'retryCount'>) => Promise<void>;
}

const SyncContext = createContext<SyncContextType | null>(null);

export function SyncProvider({ children }: { children: ReactNode }) {
  const { isOnline } = useOffline();
  const { isAuthenticated } = useAuth();
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  // Update pending count
  const updatePendingCount = useCallback(async () => {
    try {
      const count = await syncManager.getPendingCount();
      setPendingCount(count);
    } catch (error) {
      console.error('Error getting pending count:', error);
    }
  }, []);

  // Sync function
  const syncNow = useCallback(async (): Promise<SyncResult> => {
    if (!isOnline || !isAuthenticated) {
      return { success: false, message: 'No hay conexión o no está autenticado' };
    }

    if (isSyncing) {
      return { success: false, message: 'Sincronización en progreso' };
    }

    setIsSyncing(true);
    setSyncError(null);

    try {
      const result = await syncManager.processQueue();
      setLastSyncTime(new Date());
      await updatePendingCount();
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error de sincronización';
      setSyncError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setIsSyncing(false);
    }
  }, [isOnline, isAuthenticated, isSyncing, updatePendingCount]);

  // Add item to queue
  const addToQueue = useCallback(
    async (item: Omit<SyncQueueItem, 'id' | 'timestamp' | 'status' | 'retryCount'>) => {
      await syncManager.addToQueue(item);
      await updatePendingCount();

      // Try to sync immediately if online
      if (isOnline && isAuthenticated) {
        syncNow();
      }
    },
    [isOnline, isAuthenticated, syncNow, updatePendingCount]
  );

  // Auto-sync when coming online
  useEffect(() => {
    const handleOnline = () => {
      if (isAuthenticated && pendingCount > 0) {
        syncNow();
      }
    };

    window.addEventListener('app:online', handleOnline);
    return () => window.removeEventListener('app:online', handleOnline);
  }, [isAuthenticated, pendingCount, syncNow]);

  // Auto-sync interval
  useEffect(() => {
    if (!isOnline || !isAuthenticated) return;

    const interval = setInterval(() => {
      if (pendingCount > 0) {
        syncNow();
      }
    }, SYNC_CONFIG.autoSyncInterval);

    return () => clearInterval(interval);
  }, [isOnline, isAuthenticated, pendingCount, syncNow]);

  // Initial pending count
  useEffect(() => {
    updatePendingCount();
  }, [updatePendingCount]);

  return (
    <SyncContext.Provider
      value={{
        pendingCount,
        isSyncing,
        lastSyncTime,
        syncError,
        syncNow,
        addToQueue,
      }}
    >
      {children}
      {(isSyncing || pendingCount > 0) && (
        <SyncIndicator isSyncing={isSyncing} pendingCount={pendingCount} />
      )}
    </SyncContext.Provider>
  );
}

export function useSync() {
  const context = useContext(SyncContext);
  if (!context) {
    throw new Error('useSync must be used within a SyncProvider');
  }
  return context;
}

// Sync Indicator Component
function SyncIndicator({
  isSyncing,
  pendingCount,
}: {
  isSyncing: boolean;
  pendingCount: number;
}) {
  return (
    <div className="sync-indicator">
      {isSyncing ? (
        <>
          <div className="spinner" />
          <span className="text-sm">Sincronizando...</span>
        </>
      ) : (
        <>
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="text-sm">{pendingCount} pendiente{pendingCount !== 1 ? 's' : ''}</span>
        </>
      )}
    </div>
  );
}
